/**
 * What the ad groups section decides, out of React so a runner can read it.
 *
 * One decision, and it is the whole of the write half: what a stop **resulted in**.
 * The same arrangement as `campaigns.ts`' `statusOutcome`, and the same reason — a
 * screen that branches on a raw status and body inside a component cannot be tested
 * without a browser, so the branching lives here and the component renders it.
 *
 * 🔴 **The cases below are about the consequence, never about the call.** A test that
 * asserts `api.setAdGroupStatus` was invoked proves a request left; it says nothing
 * about what the screen then tells an operator, which is the thing that can be wrong
 * while every call is made correctly (twads#126 — a control present and not working).
 * So `stopOutcome` maps the answer to an outcome, `mayStopAgain` says whether the
 * control is offered again, and both are asserted directly.
 */
import type { AdGroupSwitch } from "./api.ts";

export type StopOutcome =
	/** 200: TikTok reports the ad group as this console asked. */
	| { kind: "set"; id: string; operationStatus: string; secondaryStatus: string | null }
	/**
	 * 🔴 502 with `operationStatus: "FROZEN"` — its own outcome, not a mismatch.
	 *
	 * The ad group is terminated and cannot be enabled, so it is not delivering: the
	 * operator's goal is met. But the reported status is not the one asked for and never
	 * will be, so this is neither a success to tick off nor a disagreement to look at
	 * again. Folded into `mismatch` it would invite a retry that cannot change anything;
	 * folded into `set` it would claim TikTok said DISABLE when it did not.
	 */
	| { kind: "terminated"; id: string; secondaryStatus: string | null; error: string }
	/** 502 with any other `operationStatus`, `null` when the read-back found nothing. */
	| {
			kind: "mismatch";
			operationStatus: string | null;
			secondaryStatus: string | null;
			error: string;
	  }
	/** Our own refusal, or TikTok's forwarded: the write did not happen. */
	| { kind: "refused"; status: number; error: string };

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

const errorOf = (b: Record<string, unknown>, status: number): string =>
	typeof b.error === "string" ? b.error : `the request failed (${status})`;

/**
 * The answer to `POST /adgroups/:id/status`, read as an outcome.
 *
 * The read-back's 502 is told from any other 502 the way the campaign's is: only it
 * carries `operationStatus`. `FROZEN` is then split off before the mismatch, because
 * the two call for opposite things.
 */
export function stopOutcome(status: number, body: unknown): StopOutcome {
	const b = record(body);
	const secondaryStatus = typeof b.secondaryStatus === "string" ? b.secondaryStatus : null;
	const operationStatus = typeof b.operationStatus === "string" ? b.operationStatus : null;

	if (status === 200 && typeof b.id === "string" && operationStatus !== null) {
		return { kind: "set", id: b.id, operationStatus, secondaryStatus };
	}
	if (status === 502 && "operationStatus" in b) {
		if (operationStatus === "FROZEN") {
			return {
				kind: "terminated",
				id: typeof b.id === "string" ? b.id : "",
				secondaryStatus,
				error: errorOf(b, status),
			};
		}
		return { kind: "mismatch", operationStatus, secondaryStatus, error: errorOf(b, status) };
	}
	return { kind: "refused", status, error: errorOf(b, status) };
}

/**
 * Whether the stop control is offered again after this outcome.
 *
 * 🔴 **A stop is the one write in v1 a caller may retry freely** — it asks for a state,
 * and asking twice asks for the same state — so this is not a lock against making a
 * second object, the way the campaign create's is. It is a refusal to offer an action
 * that cannot succeed:
 *
 *   - `set`: already off. Offering it again is not unsafe, only pointless.
 *   - `terminated`: the reported status will never become `DISABLE`. Offering it again
 *     invites an operator to keep pressing something that cannot change, which is how a
 *     screen teaches people to distrust it.
 *   - `mismatch`: the state is not what was asked and the retry is safe. Offered.
 *   - `refused` with 400: this console refused the request itself — `ENABLE` by name,
 *     `DELETE` by name, a missing advertiser — and the same body will be refused again.
 *     Any other status (403, 404, 409, 502 from the platform) may be transient or may be
 *     fixed elsewhere, so it is offered.
 */
export function mayStopAgain(outcome: StopOutcome): boolean {
	if (outcome.kind === "mismatch") return true;
	if (outcome.kind === "refused") return outcome.status !== 400;
	return false;
}

/** The only status this screen sends. There is no counterpart that starts one. */
export const STOP: AdGroupSwitch = "DISABLE";
